import React, { useEffect, useState } from "react";
import { Wallet, Star, Hammer, Clock, MessageSquare, FileDown, FileSpreadsheet, TrendingUp, TrendingDown, Loader2, Target, Trophy, PieChart as PieIcon, Pencil, Check } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_STYLE = {
  "Demande": "bg-brand-soft text-brand-green",
  "Devis proposé": "bg-blue-100 text-blue-700",
  "Acompte payé": "bg-amber-100 text-amber-700",
  "Terminé": "bg-brand-lime/30 text-brand-greenDeep",
};

const PERIODS = [
  { v: 3, label: "3 mois" },
  { v: 6, label: "6 mois" },
  { v: 12, label: "12 mois" },
];

const DOMAIN_COLORS = ["#c9a24b", "#3f5c4e", "#8ab17d", "#d9b370", "#5a7d6b", "#e0cfa0", "#b8863b"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-brand-soft">
      <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${accent}`}><Icon className="w-5 h-5" /></span>
      <p className="text-3xl font-bold text-brand-greenDeep">{value}</p>
      <p className="text-sm text-brand-green/60 mt-1">{label}</p>
    </div>
  );
}

function GrowthBadge({ growth }) {
  if (growth === null || growth === undefined) return null;
  const positive = growth >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      data-testid="growth-badge"
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
        positive ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-red-100 text-red-600"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {positive ? "+" : ""}{growth}%
    </span>
  );
}

export default function ActiviteArtisan() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState("");
  const [months, setMonths] = useState(6);
  const [chartLoading, setChartLoading] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  useEffect(() => {
    setChartLoading(true);
    api.get(`/works/artisan-stats?months=${months}`)
      .then((r) => { setData(r.data); setGoalInput(String(r.data.monthly_goal || "")); })
      .catch(() => {})
      .finally(() => setChartLoading(false));
  }, [months]);

  const saveGoal = async () => {
    const val = parseFloat(goalInput);
    if (isNaN(val) || val < 0) { toast.error("Saisissez un montant valide"); return; }
    setSavingGoal(true);
    try {
      await api.put("/works/artisan-goal", { goal: val });
      setData((d) => ({ ...d, monthly_goal: val }));
      setEditingGoal(false);
      toast.success("Objectif mensuel enregistré");
    } catch (e) {
      toast.error("Impossible d'enregistrer l'objectif");
    } finally {
      setSavingGoal(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/works/artisan-export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `360immoinvest-activite.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format === "xlsx" ? "Excel" : "PDF"} téléchargé`);
    } catch (e) {
      toast.error("Échec de l'export, réessayez.");
    } finally {
      setExporting("");
    }
  };

  if (!data) return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;

  const monthly = data.monthly_revenue || [];
  const hasRevenue = monthly.some((m) => m.revenue > 0);
  const growth = data.month_growth;
  const goal = data.monthly_goal || 0;
  const curMonth = data.cur_month_revenue || 0;
  const goalPct = goal > 0 ? Math.min(100, Math.round((curMonth / goal) * 100)) : 0;
  const topClients = data.top_clients || [];
  const domains = data.domain_breakdown || [];
  const domainTotal = domains.reduce((s, d) => s + d.revenue, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-3">
        <button
          data-testid="export-pdf-btn"
          onClick={() => handleExport("pdf")}
          disabled={!!exporting}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-greenDeep text-brand-lime font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Export PDF
        </button>
        <button
          data-testid="export-excel-btn"
          onClick={() => handleExport("xlsx")}
          disabled={!!exporting}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-brand-soft text-brand-greenDeep font-medium text-sm hover:bg-brand-softer transition-colors disabled:opacity-50"
        >
          {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export Excel
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-brand-soft">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 bg-brand-lime/30 text-brand-greenDeep"><Wallet className="w-5 h-5" /></span>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-3xl font-bold text-brand-greenDeep">{data.revenue.toLocaleString("fr-FR")} €</p>
            <GrowthBadge growth={growth} />
          </div>
          <p className="text-sm text-brand-green/60 mt-1">Revenus perçus (net)</p>
          <p className="text-xs text-brand-green/45 mt-0.5" data-testid="growth-caption">
            {growth >= 0 ? "En hausse" : "En baisse"} vs mois précédent
          </p>
        </div>
        <Stat icon={Clock} label="Demandes en attente" value={data.pending} accent="bg-amber-50 text-amber-600" />
        <Stat icon={Hammer} label="Chantiers en cours" value={data.active} accent="bg-blue-50 text-blue-600" />
        <Stat icon={Star} label="Note moyenne" value={data.avg_rating ? `${data.avg_rating}/5` : "—"} accent="bg-brand-soft text-brand-green" />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="revenue-chart-card">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-1 flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Évolution des revenus</h3>
            <p className="text-sm text-brand-green/50">Revenus nets perçus sur la période sélectionnée</p>
          </div>
          <div className="inline-flex rounded-xl bg-brand-softer p-1" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.v}
                data-testid={`period-${p.v}`}
                onClick={() => setMonths(p.v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  months === p.v ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/60 hover:text-brand-greenDeep"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {hasRevenue ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9a24b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#c9a24b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8a8a80" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#8a8a80" }} axisLine={false} tickLine={false} width={68} tickFormatter={(v) => `${Number(v).toLocaleString("fr-FR")} €`} />
              <Tooltip
                formatter={(v) => [`${Number(v).toLocaleString("fr-FR")} €`, "Revenus nets"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #efe7d6", fontSize: 13 }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#c9a24b" strokeWidth={2.5} fill="url(#rev)" isAnimationActive={!chartLoading} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex flex-col items-center justify-center text-brand-green/40 text-sm">
            <TrendingUp className="w-8 h-8 mb-2 opacity-40" />
            Aucun revenu enregistré sur cette période.
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="goal-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2"><Target className="w-5 h-5" /> Objectif mensuel</h3>
            {!editingGoal && (
              <button data-testid="edit-goal-btn" onClick={() => setEditingGoal(true)} className="text-brand-green/50 hover:text-brand-greenDeep transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>

          {editingGoal ? (
            <div className="space-y-3">
              <label className="text-sm text-brand-green/60">Revenu net visé par mois (€)</label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="goal-input"
                  type="number"
                  min="0"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="Ex : 5000"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-brand-soft bg-brand-softer outline-none text-brand-greenDeep focus:border-brand-limeDark"
                />
                <button data-testid="save-goal-btn" onClick={saveGoal} disabled={savingGoal} className="px-3 py-2.5 rounded-xl bg-brand-greenDeep text-brand-lime disabled:opacity-50">
                  {savingGoal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : goal > 0 ? (
            <div>
              <div className="flex items-end justify-between mb-2">
                <p className="text-3xl font-bold text-brand-greenDeep" data-testid="goal-current">{curMonth.toLocaleString("fr-FR")} €</p>
                <p className="text-sm text-brand-green/50">/ {goal.toLocaleString("fr-FR")} €</p>
              </div>
              <div className="h-3 rounded-full bg-brand-softer overflow-hidden" data-testid="goal-bar">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand-limeDark to-brand-lime transition-all duration-700"
                  style={{ width: `${goalPct}%` }}
                />
              </div>
              <p className="text-sm mt-2 text-brand-green/60" data-testid="goal-pct">
                {goalPct}% de l'objectif atteint ce mois-ci
                {goalPct >= 100 && <span className="ml-1 font-semibold text-brand-greenDeep">🎯 Objectif atteint !</span>}
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Target className="w-8 h-8 mx-auto mb-2 text-brand-green/30" />
              <p className="text-sm text-brand-green/50 mb-3">Aucun objectif défini</p>
              <button data-testid="set-goal-btn" onClick={() => setEditingGoal(true)} className="px-4 py-2 rounded-xl bg-brand-greenDeep text-brand-lime text-sm font-medium">
                Fixer un objectif
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="domain-card">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><PieIcon className="w-5 h-5" /> Répartition par domaine</h3>
          {domains.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={domains} dataKey="revenue" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={70} paddingAngle={2}>
                    {domains.map((d, i) => <Cell key={d.name} fill={DOMAIN_COLORS[i % DOMAIN_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString("fr-FR")} €`} contentStyle={{ borderRadius: 12, border: "1px solid #efe7d6", fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {domains.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-brand-green/70">
                      <span className="w-3 h-3 rounded-sm" style={{ background: DOMAIN_COLORS[i % DOMAIN_COLORS.length] }} />
                      {d.name}
                    </span>
                    <span className="font-medium text-brand-greenDeep">{domainTotal > 0 ? Math.round((d.revenue / domainTotal) * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[160px] flex flex-col items-center justify-center text-brand-green/40 text-sm">
              <PieIcon className="w-8 h-8 mb-2 opacity-40" />
              Aucun revenu sur cette période.
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="top-clients-card">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Trophy className="w-5 h-5" /> Meilleurs clients</h3>
          <div className="space-y-3">
            {topClients.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3" data-testid={`top-client-${i}`}>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                  i === 0 ? "bg-brand-lime text-brand-greenDeep" : "bg-brand-softer text-brand-green/60"
                }`}>{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-brand-greenDeep truncate">{c.name}</span>
                <span className="text-sm font-semibold text-brand-greenDeep">{c.revenue.toLocaleString("fr-FR")} €</span>
              </div>
            ))}
            {topClients.length === 0 && (
              <div className="h-[120px] flex flex-col items-center justify-center text-brand-green/40 text-sm">
                <Trophy className="w-8 h-8 mb-2 opacity-40" />
                Aucun client sur cette période.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-brand-soft">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Hammer className="w-5 h-5" /> Chantiers récents</h3>
          <div className="space-y-3">
            {data.recent_works.map((w) => (
              <div key={w.id} className="flex items-center justify-between py-2 border-b border-brand-soft last:border-0">
                <div>
                  <p className="text-sm font-medium text-brand-greenDeep">{w.client_name}</p>
                  <p className="text-xs text-brand-green/50 truncate max-w-[220px]">{w.description}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[w.status] || "bg-brand-soft"}`}>{w.status}</span>
              </div>
            ))}
            {data.recent_works.length === 0 && <p className="text-sm text-brand-green/50">Aucun chantier pour le moment.</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-brand-soft">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Derniers avis ({data.review_count})</h3>
          <div className="space-y-3">
            {data.reviews.map((rv) => (
              <div key={rv.id} className="rounded-xl bg-brand-softer p-3">
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i <= rv.rating ? "fill-brand-limeDark text-brand-limeDark" : "text-brand-soft"}`} />
                  ))}
                  <span className="text-xs text-brand-green/50 ml-1">{rv.client_name}</span>
                </div>
                {rv.comment && <p className="text-sm text-brand-green/70 italic">« {rv.comment} »</p>}
              </div>
            ))}
            {data.reviews.length === 0 && <p className="text-sm text-brand-green/50">Pas encore d'avis.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
